import type { Selector } from '@ngrx/store';

import type { ConfigObject, MakeRef, MetaState, PaginationState, Reference } from '../types';

export type ID = string | number;
export const DEFAULT_SCOPE_NAME = 'main';
export type Scope = typeof DEFAULT_SCOPE_NAME | string;

export type GenericIdSelector<Entity extends GenericEntity> = (entity: Entity) => ID;

export type DefaultIdSelector<Entity extends GenericEntity> = Entity extends {
  id: ID;
} & GenericEntity
  ? GenericIdSelector<{ id: Entity['id'] }>
  : GenericIdSelector<Entity>;

export type IdSelectorOrDefault<
  IdSelector extends GenericIdSelector<Entity> | undefined,
  Entity extends GenericEntity,
> = IdSelector extends GenericIdSelector<Entity> ? IdSelector : DefaultIdSelector<Entity>;

export type OptimisticUpdate<Entity extends GenericEntity> = {
  update: EntityPartial<Entity>;
  before: Entity;
};

/**
 * Entity collection store structure
 */
export interface EntityState<
  Entity extends GenericEntity,
  MetaOptions extends GenericMetaOptions = GenericMetaOptions,
> {
  /**
   * We keep only one instance of given entity inside Record<Entity.ID, Entity> map
   * if entity is updated it will be updated in every scope
   */
  entityMap: Record<ID, Entity | null>;
  /**
   * For each entity here we keep meta info like isLoaded or isPending
   */
  entityMetaMap: Record<ID, MetaState>;
  /**
   * Scope is an array of entity ids, by default store is created with "main" scope
   * You can create multiple scopes for each entity, for example you want to load
   * all notifications and unread notifications so in effects you should dispatch
   * @example
   * actions.fetchCollectionSuccess({ payload: { data, scope: 'all' } })
   * actions.fetchCollectionSuccess({ payload: { data, scope: 'unread' } })
   * this is how state may look like after this
   * {
   *   ...
   *   entityMap: {
   *     1: { id: 1, ... },
   *     2: { id: 2, ... },
   *     3: { id: 3, ... },
   *     4: { id: 4, ... },
   *   }
   *   scope: {
   *     all: [1, 2, 3, 4],
   *     unread: [2, 4],
   *   }
   *   ...
   * }
   * then you can select
   * selectors.entities({ scope: 'all' })
   * selectors.entities({ scope: 'unread' })
   *
   */
  scope: Record<Scope, ID[]>;
  /**
   * Contains meta info for whole scope (isLoaded, isPending etc)
   */
  scopeMeta: Record<Scope, MetaState<InferScopeMeta<MetaOptions>>>;
  /**
   * You can create alias for entity before entity exist for example
   * actions.createOnePessimistic will require correlationId so you can track if
   * entity is created or errored
   * @example
   *
   * actions.createOnePessimistic({ payload: { correlationId, data: { ... } } });
   * then you can select it by correlationId selectors.byId(correlationId)
   * this is possible through this aliases map which stores Record<correlationId, realId>
   */
  aliases: Record<ID, ID>;
  /**
   * This map contains copy of entity before optimistic update this is done to
   * rollback update if server won't accept it
   */
  optimisticUpdate: Record<string, OptimisticUpdate<Entity>>;
  /**
   * This map contains copy of entity before optimistic delete  this is done to
   * rollback delete if server won't accept it
   */
  optimisticDelete: Record<ID, Entity>;
  /**
   * Here pagination metadata is stored
   */
  scopePagination: Record<Scope, PaginationState>;
}

/**
 * @see {@link EntityState#aliases}
 */
export interface CorrelationId {
  correlationId?: string;
}

export interface EntityAdapterOptions {
  scope?: string;
}

export interface PrependingOption {
  prepend?: boolean;
}

export type GenericEntity = Record<string, any>;

export interface GenericMetaOptions {
  scopeMeta?: object;
}

export type EntityPartial<Entity extends GenericEntity> = Partial<Entity>;

export type BaseSelector<
  Entity extends GenericEntity,
  MetaOptions extends GenericMetaOptions,
> = Selector<any, EntityState<Entity, MetaOptions>>;

export type MetaScopeOptions = { scope: Scope | null };
export type MetaEntitiesOptions = { entities: ID[] };
export type MetaActionOptions = MetaScopeOptions & MetaEntitiesOptions;

export type InferScopeMeta<T> = T extends { scopeMeta?: infer R } ? R : never;

/**
 * Set of helpers that will make it easier to modify state
 */
export interface EntityMetaAdapter<
  Entity extends GenericEntity,
  MetaOptions extends GenericMetaOptions,
> {
  markAsPending: <T1 extends GenericEntity>(
    state: EntityState<T1, MetaOptions>,
    options?: MetaActionOptions,
  ) => EntityState<T1, MetaOptions>;
  markAsLoaded: <T1 extends GenericEntity>(
    state: EntityState<T1, MetaOptions>,
    options?: MetaActionOptions,
  ) => EntityState<T1, MetaOptions>;
  setError: (
    state: EntityState<Entity, MetaOptions>,
    error: any,
    options?: MetaActionOptions,
  ) => EntityState<Entity, MetaOptions>;
  setPaginationMeta: (
    state: EntityState<Entity, MetaOptions>,
    paginationMeta: PaginationState,
    options?: MetaScopeOptions,
  ) => EntityState<Entity, MetaOptions>;
  // alias correlationId -> id for remote create operations
  setAlias: (
    state: EntityState<Entity, MetaOptions>,
    data: Record<ID, ID>,
  ) => EntityState<Entity, MetaOptions>;
  setScopeMetaCustom: (
    state: EntityState<Entity, MetaOptions>,
    data: InferScopeMeta<MetaOptions>,
    options?: MetaScopeOptions,
  ) => EntityState<Entity, MetaOptions>;
}

/**
 * Set of helpers that will make it easier to modify state
 */
export interface EntityDataAdapter<
  Entity extends GenericEntity,
  MetaOptions extends GenericMetaOptions,
  State extends EntityState<Entity, MetaOptions> = EntityState<Entity, MetaOptions>,
> {
  // Add to scope (uniq), set entity
  addOne: (
    state: State,
    data: Entity,
    options?: EntityAdapterOptions & CorrelationId & PrependingOption,
  ) => State;
  addMany: (
    state: State,
    data: Entity[],
    options?: EntityAdapterOptions & PrependingOption,
  ) => State;

  // Set scope, set entity
  setOne: (state: State, data: Entity, options?: EntityAdapterOptions) => State;
  setMany: (state: State, data: Entity[], options?: EntityAdapterOptions) => State;

  // Doesn't touch scope, set entity
  writeOne: (state: State, data: Entity) => State;
  writeMany: (state: State, data: Entity[]) => State;

  // Replace entity in all scopes, set entity
  replaceOne: (state: State, previousId: ID, current: Entity) => State;
  replaceMany: (state: State, data: Array<{ previousId: ID; current: Entity }>) => State;

  // Removes entity from all scopes, removes entity
  removeOne: (state: State, id: ID) => State;
  removeMany: (state: State, ids: ID[]) => State;

  // Doesn't touch scope, updates entity (spread)
  updateOne: (state: State, data: EntityPartial<Entity>) => State;
  updateMany: (state: State, data: Array<EntityPartial<Entity>>) => State;

  // Doesn't touch scope, updates or creates entity (spread)
  updateOrCreateMany: (state: State, data: Entity[]) => State;

  // Add to scope if not exist, update or add entity
  upsertOne: (state: State, data: Entity, options?: EntityAdapterOptions) => State;
  upsertMany: (state: State, data: Entity[], options?: EntityAdapterOptions) => State;

  optimisticUpdateSet: (state: State, correlationId: ID, data: OptimisticUpdate<Entity>) => State;
  optimisticUpdateRemove: (state: State, correlationId: string) => State;
  optimisticDeleteSet: (state: State, data: Entity) => State;
  optimisticDeleteRemove: (state: State, id: ReturnType<GenericIdSelector<Entity>>) => State;

  // Remove all entities from scope
  resetScope: (state: State, scope?: Scope) => State;
  // Reset to initial state
  resetAll: (state: State) => State;
}

export type EntityCollectionAdapter<
  Entity extends GenericEntity,
  MetaOptions extends GenericMetaOptions,
> = EntityMetaAdapter<Entity, MetaOptions> & EntityDataAdapter<Entity, MetaOptions>;

export type EntityCollectionCustomActionsCreator<
  Entity extends GenericEntity,
  MetaOptions extends GenericMetaOptions,
> = (
  adapter: EntityCollectionAdapter<Entity, MetaOptions>,
) => ConfigObject<EntityState<Entity, MetaOptions>>;

export interface EntityCollectionStoreOptions<
  Entity extends GenericEntity,
  MetaOptions extends GenericMetaOptions,
> {
  /**
   * Selector that will point to root of entity state
   * by default root[entityNamespace]
   */
  baseSelector?: (root: any) => EntityState<Entity>;
  /**
   * Provide function that will return unique identifier for given entity
   * by default Entity => Entity.id
   */
  idSelector?: GenericIdSelector<Entity>;
  /**
   * You can add your own actions or overwrite the default actions,
   * to make it easier to modify state, state adapter function is given
   * @see {@link EntityDataAdapter}
   * @see {@link EntityMetaAdapter}
   * @example
   * customActions: adapter => ({
   *   // custom
   *   fetchLast: (state, payload: {uuid: string}) => adapter.markAsPending(state, {entities: [payload.uuid]})
   *   // overwrite
   *   fetchOne: (state, payload: { id: number, prop: string }) => adapter.markAsPending(state, {entities: [payload.id]}),
   * })
   */
  customActions?: EntityCollectionCustomActionsCreator<Entity, MetaOptions>;
  /**
   * Use references to define relationship between entities, example:
   * makeRef => [
   *   makeRef('adminId', 'admin', UserSelectors.byId)
   * ]
   */
  references?: (makeRef: MakeRef<Entity>) => Array<Reference<Entity>>;
  /**
   * Store initial state by default {@link entityInitialState}
   */
  initialState?: EntityState<Entity>;
}

export type EntityCollectionStoreCustomActions<
  StoreOptions extends EntityCollectionStoreOptions<any, any>,
> =
  StoreOptions['customActions'] extends EntityCollectionCustomActionsCreator<any, any>
    ? ReturnType<StoreOptions['customActions']>
    : {};
